import { Col, Icon, Row, Spin } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ApplicationContext } from "../context";
import { BrandType } from "../types/enumerations/common";
import { Brand, Onload, IframeParams } from "../types/interfaces/onload";
import { DataUtils } from "../utilities";
import { getAuthenticationDetails } from "../utilities/authentication";
import { NotificationUtils } from "@yodlee/common-components/dist/utilities";

declare global {
    interface Window {
        IframeApp: any
    }
}

enum UserType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    ADMIN = "ADMIN"
}

enum DestinationBrandType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    SUB_BRAND = "SUB_BRAND"
}

type IframeProps = {
    app: string,
    apiUrl?: string,
    appUrl: string,
    yslUrl?: string,
    accessToken?: string,
    fastlinkUrl?: string,
    rsession: string,
    yccUrl: string,
    brandId: string,
    appId: string,
    brandName: string,
    environment?: string,
    region?: string,
    locationUrl: string,
    destinationCobrandId?: string | undefined,
    destinationCobrandType?: string | undefined,
    destinationChannelId?: string | undefined,
    extraParams?: IframeParams,
    finappUrl?: string
}

type Token = {
    appId: string,
    brandId: number,
    brandName: string,
    environment?: string,
    region?: string,
    appUrl: string,
    yslUrl: string,
    apiUrl: string,
    finappUrl: string,
    accessToken: string,
    fastlinkUrl: string,
    rsession: string,
    yccUrl: string,
    smbUrl?: string,
    smbAppUrl?: string,
    advisorInsightsUrl?: string,
    refreshToken?: string,
    clientId?: string,
    supportingChannelId?: number,
    destinationCobrandId?: string | undefined
}

interface IProps {
    brand: Brand & { channel?: Brand },
    extraParams: IframeParams
    iFrame: {
        id: string,
        name: string
    },
    tokenParams?: Array<string>
    tokenBrandSpecific?: boolean
    omitDestinationContext?: boolean
    scrolling?: "auto" | "yes" | "no"
    onDataChanged?: (isChanged: boolean, extraParams?: string) => void,
    onBrandChange?: (brand: Brand & { channel?: Brand }, extraParams?: string) => void
}


const postForm = (path: string, params: any, method = "POST", target: string = "_blank") => {
    const form = document.createElement('form');
    form.method = method;
    form.action = path;
    form.target = target;
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = params[key];
            form.appendChild(hiddenField);
        }
    }
    document.body.appendChild(form);
    form.submit();
}

const IframeComponent: React.FC<IProps> = ({
    brand,
    extraParams,
    iFrame,
    onDataChanged,
    onBrandChange,
    tokenParams,
    tokenBrandSpecific = true,
    omitDestinationContext = false,
    scrolling = "no"
}) => {
    const { permissions, onload } = useContext(ApplicationContext);

    const [isFrameLoading, setIsFrameLoading] = useState<boolean>(true);
    const [token, setToken] = useState<Token | null>();
    const tokenRequestViewRef = useRef<string | undefined>(extraParams.view);
    const loadIframeScript = (scriptBaseUrl: string) => {
        if (!window.IframeApp) {
            const urlOrigin = new URL(scriptBaseUrl)?.origin;
            const scriptUrl = urlOrigin + "/js/post-message.js"
            const script = document.createElement("script");
            script.src = scriptUrl;
            script.onload = () => {
                if (window.IframeApp) {
                    window.IframeApp.initialize({
                        "name": "myyodlee",
                        "attributes": {
                            "iframeId": iFrame.id
                        },
                        "eventHandlers": {
                            "error": function (eventData: any) {
                            },
                            "renew_session": function (eventData: any) {
                            }
                        }
                    })
                }
            }
            document.head.appendChild(script);
        } else {
            window.IframeApp.initialize({
                "name": "myyodlee",
                "attributes": {
                    "iframeId": iFrame.id
                },
                "eventHandlers": {
                    "error": function (eventData: any) {
                    },
                    "renew_session": function (eventData: any) {
                    }
                }
            })
        }
    }

    const loadIframe = () => {
        if (onload?.financialInstitution) {
            
            const isAllCustomersSelected = [BrandType.YODLEE, BrandType.CHANNEL].includes(onload?.financialInstitution?.type?.id) && !brand?.id;

            if ((!isAllCustomersSelected || brand) && token) {
                setIsFrameLoading(true);
                const authenticateUrl = token.appUrl;
                loadIframeScript(authenticateUrl);
                let props: IframeProps = {
                    rsession: token.rsession,
                    yccUrl: token.yccUrl,
                    brandId: token.brandId + "",
                    appId: token.appId,
                    brandName: token?.brandName,
                    ...token.environment && {environment: token.environment},
                    ...token.region && {region: token.region},
                    locationUrl: window.origin,
                    appUrl: token.appUrl,
                    ...token.apiUrl && {apiUrl: token.apiUrl},
                    ...token.yslUrl && {yslUrl: token.yslUrl},
                    ...token.finappUrl && {finappUrl: token.finappUrl},
                    ...token.smbAppUrl && {smbAppUrl: token.smbAppUrl},
                    ...token.smbUrl && {smbUrl: token.smbUrl},
                    ...token.refreshToken && {refreshToken: token.refreshToken},
                    ...token.clientId && {clientId: token.clientId},
                    ...token.advisorInsightsUrl && {advisorInsightsUrl: token.advisorInsightsUrl},
                    ...token.accessToken && {accessToken: token.accessToken},
                    ...token.fastlinkUrl && {fastlinkUrl: token.fastlinkUrl},
                    app: extraParams.app ? extraParams.app : extraParams.view
                };

                if (!omitDestinationContext && brand.id) {
                    const brandType = onload?.financialInstitution.type.id;

                    //setting destination channel ID
                    if (brandType === BrandType.SUB_BRAND) {
                        props.destinationChannelId = token?.supportingChannelId?.toString() || ""
                    } else if (brand?.type?.id === BrandType.SUB_BRAND) {
                        if (brand?.channel?.id) {
                            props.destinationChannelId = brand.channel?.id?.toString() || "";
                        }
                        else {
                            props.destinationChannelId = getChannelForSubBrand(brand?.id)?.id?.toString() || ""
                        }
                    }

                    switch (brand?.type.id) {
                        case BrandType.CHANNEL:
                            props.destinationCobrandType = DestinationBrandType.CHANNEL;
                            break;
                        case BrandType.SUB_BRAND:
                            props.destinationCobrandType = DestinationBrandType.SUB_BRAND;
                            break;
                        default:
                            props.destinationCobrandType = DestinationBrandType.COBRAND;
                    }

                    props.destinationCobrandId = brand.id;
                }

                postForm(authenticateUrl, props, "POST", iFrame.name);
            }
        }
    }

    const findBrandById = (onload: Onload, brandId: number) => {
        if (onload.financialInstitution?.id === brandId) {
            return onload.financialInstitution;
        }

        let returnBrand: Brand | undefined = undefined;
        onload.financialInstitution?.brands?.find(brand => {
            if (brand.id === brandId) {
                returnBrand = brand;
                return true;
            }
            else if (brand.type?.id === BrandType.CHANNEL) {
                const foundSubBrand = brand.brands?.find(subBrand => {
                    if (subBrand.id === brandId) {
                        returnBrand = subBrand;
                        return true;
                    }
                })
                if (foundSubBrand) {
                    return true;
                }
            }
        });

        return returnBrand;
    }

    const getChannelForSubBrand = (brandId: number): Brand | undefined => {
        const brandType = onload?.financialInstitution.type.id;
        if (brandType === BrandType.YODLEE) {
            let channel: Brand | undefined = undefined;
            onload?.financialInstitution?.brands?.find(brand => {
                if (brand.type.id === BrandType.CHANNEL) {
                    const foundSubBrand = brand.brands?.find(subBrand => {
                        if (subBrand.id === brandId) {
                            channel = brand;
                            return true;
                        }
                    })
                    if (foundSubBrand) {
                        return true;
                    }
                }
            });
            return channel;
        } else if (brandType === BrandType.CHANNEL && onload?.financialInstitution.id !== brandId) {
            return onload?.financialInstitution as Brand;
        } else {
            return undefined;
        }
    }

    useEffect(() => {
        const requestView = extraParams.view;
        tokenRequestViewRef.current = requestView;
        setToken(null);
        const authentication = getAuthenticationDetails(brand);
        DataUtils.configToolToken(authentication).get(
            { "view": extraParams.view, "app": extraParams.appName, ...tokenParams },
            tokenBrandSpecific
        )
        .then((token) => {
            if (tokenRequestViewRef.current !== requestView) return;
            setToken(token);
        })
        .catch(() => {
            if (tokenRequestViewRef.current !== requestView) return;
            NotificationUtils.error("Request failed.", "Functionality currently unavailable", "ghost");
        });

        const messageHandler = (event: any) => {
            if (event?.data?.eventName === "renew_session") {
                window.dispatchEvent(new Event('resetTimer'));
            }
            else if (event?.data?.eventName === "error") {
                window.dispatchEvent(new Event('unauthorisedAccess'));
            }
            else if (event?.data?.eventName === "app_data_change") {
                if (onDataChanged) {
                    const changeState: boolean = event?.data?.eventData?.changed;
                    onDataChanged(changeState);
                }
            }
        }

        window.addEventListener("message", messageHandler);

        return () => {
            tokenRequestViewRef.current = undefined;
            window.removeEventListener("message", messageHandler);
        };
    }, [brand.id, extraParams.view, tokenParams, tokenBrandSpecific, omitDestinationContext]);

    useEffect(() => {
        if (token) {
            loadIframe();
        }
    }, [token]);

    return (
        onload && permissions && token ?
            <Spin
                size="large"
                indicator={<Icon type="loading" />}
                spinning={isFrameLoading}
                wrapperClassName="spinner-config-tool"
            >
                <iframe
                    width="100%"
                    height="1250"
                    marginHeight={0}
                    marginWidth={0}
                    frameBorder={0}
                    title="Iframe tool container"
                    scrolling={scrolling}
                    id={iFrame.id}
                    name={iFrame.name}
                    onLoad={() => setIsFrameLoading(false)}
                />
            </Spin> :
            <Row type="flex" style={{ height: "80vh" }} justify="center" align="middle">
                <Col>
                    <Spin size="large" indicator={<Icon type="loading" />} />
                </Col>
            </Row>
    )
}

export default IframeComponent;